import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Check, RefreshCw, AlertCircle } from 'lucide-react';
import { tasksApi } from '../../services/api';
import UserSelector from '../shared/UserSelector';
import Avatar from '../shared/Avatar';
import EmptyState from '../shared/EmptyState';
import NewTaskModal from './NewTaskModal';
import { formatDateRelative, formatDateShort, isDeadlinePast, isDeadlineSoon } from '../../utils/formatters';
import toast from 'react-hot-toast';
import clsx from 'clsx';

type TabType = 'pending' | 'completed';
type SortType = 'oldest' | 'newest' | 'deadline';

export default function Tasks() {
  const [tab, setTab] = useState<TabType>('pending');
  const [sort, setSort] = useState<SortType>('oldest');
  const [assignedTo, setAssignedTo] = useState('all');
  const [showNewModal, setShowNewModal] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', { status: tab, sort, assigned_to: assignedTo }],
    queryFn: () => tasksApi.list({ status: tab, sort, assigned_to: assignedTo }),
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => tasksApi.complete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Tarefa concluída!');
    },
  });

  const reopenMutation = useMutation({
    mutationFn: (id: string) => tasksApi.reopen(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Tarefa reaberta');
    },
  });

  const tasks = data?.data || [];

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-4">
        {(['pending', 'completed'] as TabType[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={clsx(
              'flex-1 py-2 rounded-lg text-sm font-medium transition-colors',
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            {t === 'pending' ? 'Pendentes' : 'Concluídas'}
          </button>
        ))}
      </div>

      {/* Filter by user */}
      <UserSelector
        value={assignedTo}
        onChange={setAssignedTo}
        includeAll
        className="mb-4"
      />

      {/* Sort */}
      {tab === 'pending' && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {[
            { value: 'oldest', label: 'Mais antigas' },
            { value: 'newest', label: 'Mais recentes' },
            { value: 'deadline', label: 'Deadline' },
          ].map(s => (
            <button
              key={s.value}
              onClick={() => setSort(s.value as SortType)}
              className={clsx(
                'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                sort === s.value
                  ? 'bg-brand-500 border-brand-500 text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Task list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="card animate-pulse h-20"/>
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={Check}
          title={tab === 'pending' ? 'Nenhuma tarefa pendente' : 'Nenhuma tarefa concluída'}
          description={tab === 'pending' ? 'Clique em + para adicionar uma nova tarefa' : 'Tarefas concluídas nos últimos 30 dias aparecerão aqui'}
        />
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => {
            const overdue = task.deadline && isDeadlinePast(task.deadline) && task.status === 'pending';
            const soon = task.deadline && isDeadlineSoon(task.deadline) && task.status === 'pending';

            return (
              <div key={task.id} className={clsx('card', overdue && 'border-red-200 bg-red-50')}>
                <div className="flex items-start gap-3">
                  {/* Complete / Reopen button */}
                  <button
                    onClick={() => task.status === 'pending'
                      ? completeMutation.mutate(task.id)
                      : reopenMutation.mutate(task.id)
                    }
                    disabled={completeMutation.isPending || reopenMutation.isPending}
                    className={clsx(
                      'w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors',
                      task.status === 'completed'
                        ? 'bg-green-500 border-green-500 text-white'
                        : overdue
                        ? 'border-red-400 hover:border-red-500'
                        : 'border-gray-300 hover:border-brand-500'
                    )}
                  >
                    {task.status === 'completed' ? (
                      <Check size={12} />
                    ) : (
                      <span className="sr-only">Concluir</span>
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    <p className={clsx(
                      'text-sm font-medium',
                      task.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-800'
                    )}>
                      {task.title}
                    </p>

                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {/* Assigned to */}
                      {task.assigned_to_user && (
                        <div className="flex items-center gap-1">
                          <Avatar name={task.assigned_to_user.name} photoUrl={task.assigned_to_user.photo_url} size="xs" />
                          <span className="text-xs text-gray-500">{task.assigned_to_user.name.split(' ')[0]}</span>
                        </div>
                      )}

                      {/* Assigned by */}
                      {task.assigned_by_user && (
                        <span className="text-xs text-gray-400">por {task.assigned_by_user.name.split(' ')[0]}</span>
                      )}

                      {/* Deadline */}
                      {task.deadline && (
                        <span className={clsx(
                          'text-xs font-medium flex items-center gap-1',
                          overdue ? 'text-red-600' : soon ? 'text-orange-500' : 'text-gray-400'
                        )}>
                          {overdue && <AlertCircle size={11}/>}
                          {overdue ? 'Vencida: ' : ''}{formatDateRelative(task.deadline)}
                        </span>
                      )}

                      {/* Completed at */}
                      {task.status === 'completed' && task.completed_at && (
                        <span className="text-xs text-gray-400">
                          Concluída em {formatDateShort(task.completed_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FAB - New Task */}
      <button
        onClick={() => setShowNewModal(true)}
        className="fixed bottom-28 right-4 w-14 h-14 bg-brand-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-brand-600 active:bg-brand-700 transition-colors z-30"
        aria-label="Nova tarefa"
      >
        <Plus size={24} />
      </button>

      <NewTaskModal isOpen={showNewModal} onClose={() => setShowNewModal(false)} />
    </div>
  );
}
